import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// 🔐 USER-SCOPED THEME KEY
// IMPORTANT:
// Only trust CURRENT live auth identity.
// Prevent stale auth_user cache from bleeding themes
// across logout/login account switching.
const getUserThemeKey = (emailOverride) => {
    try {
        const email = String(emailOverride ||
            localStorage.getItem("email") ||
            sessionStorage.getItem("email") ||
            "").toLowerCase().trim();
        return email
            ? `ava_theme:${email}`
            : "ava_theme:unknown";
    }
    catch {
        return "ava_theme:unknown";
    }
};
import "../components/NeonTaskbar.css";
import "./Dashboard.css";
import "../styles/Dashboard.css";
import React, { useState, useEffect } from "react";
// import removed: null not exported from authCache
import ChatBox from "../components/ChatBox";
import AvaFace from "../components/AvaFace";
import Checkers3D from "../components/Checkers3D";
import { LayoutGrid, Power, User } from "lucide-react";
import MobileOrbMenu from "../components/MobileOrbMenu";
import ConnectionModeStatusBadge from "../components/ConnectionModeStatusBadge";
import { usePreferences, } from "../context/PreferencesContext";
import { getAvaaioUiCopy, } from "../utils/avaaioUiCopy";
import { getUnifiedToken } from "../utils/unifiedAuth";
const API_BASE = import.meta.env.VITE_API_BASE || "https://api.avaaio.com";
export default function Dashboard() {
    const { preferences } = usePreferences();
    const interfaceCopy = getAvaaioUiCopy(preferences.language);
    const [language, setLanguage] = useState("en-US");
    const [activeGame, setActiveGame] = useState(null);
    const [theme, setTheme] = useState(() => {
        try {
            const role = String(localStorage.getItem("role") || "").toLowerCase();
            const sub = String(localStorage.getItem("subscription_status") || "").toLowerCase();
            const isAdmin = role === "admin" ||
                role === "owner" ||
                role === "superadmin";
            const isPro = role === "vip" ||
                role === "pro" ||
                sub === "active";
            // Prevent stale free-mode flash for admin/pro users.
            if (isAdmin || isPro) {
                const stored = localStorage.getItem(getUserThemeKey()) ||
                    localStorage.getItem(getUserThemeKey() + ":color") ||
                    localStorage.getItem("theme_color") ||
                    localStorage.getItem("ava_theme");
                const aliases = {
                    red: "lava",
                    crimson: "lava",
                    blue: "midnight",
                    green: "emerald",
                    orange: "amber",
                    yellow: "amber",
                    gold: "amber",
                    pink: "aurora",
                    white: "cyan",
                };
                const normalized = String(stored || "cyan")
                    .trim()
                    .toLowerCase();
                return aliases[normalized] || normalized || "cyan";
            }
            return "black";
        }
        catch {
            return "black";
        }
    });
    const [avaSpeaking, setAvaSpeaking] = useState(false);
    const [mouthLevel, setMouthLevel] = useState(0);
    const [time, setTime] = useState("");
    const [taskbarVisible, setTaskbarVisible] = useState(false);
    const [menuOpen, setMenuOpen] = useState(null);
    const [me, setMe] = useState(null);
    // === ava-plan-updated listener (LIVE PLAN SYNC) ===
    useEffect(() => {
        const refreshUser = async () => {
            try {
                const token = getUnifiedToken();
                const res = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    credentials: "include",
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                });
                if (!res.ok)
                    return;
                const userData = await res.json();
                setMe(userData ?? null);
            }
            catch (err) {
                console.warn("[Dashboard] live plan sync failed:", err);
            }
        };
        window.addEventListener("ava-plan-updated", refreshUser);
        window.addEventListener("ava-force-refresh", refreshUser);
        // live plan delayed cookie sync: login/reactivation cookies may settle after route render
        const syncTimerFast = window.setTimeout(refreshUser, 400);
        const syncTimerSlow = window.setTimeout(refreshUser, 2200);
        return () => {
            window.clearTimeout(syncTimerFast);
            window.clearTimeout(syncTimerSlow);
            window.removeEventListener("ava-plan-updated", refreshUser);
            window.removeEventListener("ava-force-refresh", refreshUser);
        };
    }, []);
    const isAdmin = ["admin", "owner", "superadmin"].includes(String(me?.role || "").toLowerCase());
    const isPro = (String(me?.role || "").toLowerCase() === "vip") ||
        (String(me?.role || "").toLowerCase() === "pro") ||
        (String(me?.subscription_status || "").toLowerCase() === "active");
    if (me?.role || me?.subscription_status) {
        console.log("[DashboardAuth]", {
            role: me?.role,
            subscription_status: me?.subscription_status,
            isPro,
            isAdmin
        });
    }
    const isFree = !!me && !isAdmin && !isPro;
    // === AVAAIO SPEECH PULSE V1 ===
    //
    // Ava intentionally keeps the no-mouth appearance while
    // speaking. Speech presence is represented by light.
    useEffect(() => {
        setMouthLevel(0);
    }, [avaSpeaking]);
    useEffect(() => {
        const handler = (e) => {
            setAvaSpeaking(!!e.detail?.speaking);
        };
        window.addEventListener("ava-speaking", handler);
        return () => {
            window.removeEventListener("ava-speaking", handler);
        };
    }, []);
    useEffect(() => {
        const id = setInterval(() => {
            setTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
        }, 1000);
        return () => clearInterval(id);
    }, []);
    function handleSwitchAccount() {
        try {
            sessionStorage.setItem("ava_logged_out", "1");
        }
        catch { }
        try {
            localStorage.clear();
            sessionStorage.clear();
        }
        catch { }
        window.location.href = "/login?logged_out=1&switch=1";
    }
    // Apply theme class to body when theme changes
    useEffect(() => {
        return () => {
        };
    }, [theme]);
    // Load user + set theme from saved memory
    useEffect(() => {
        const token = getUnifiedToken();
        fetch(`${API_BASE}/api/v1/auth/me`, {
            credentials: "include",
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })
            .then(async (res) => {
            if (!res.ok) {
                throw new Error(`auth/me failed: ${res.status}`);
            }
            const userData = await res.json();
            return userData;
        })
            .then((userData) => {
            if (!userData)
                return;
            setMe(userData ?? null);
            const saved = (userData.memory) || {};
            const role = String((userData?.role) || "").toLowerCase();
            const sub = String((userData?.subscription_status) || "").toLowerCase();
            const isAdmin = ["admin", "owner", "superadmin"].includes(role);
            const isPro = role === "vip" ||
                role === "pro" ||
                sub === "active";
            const scopedTheme = (() => {
                try {
                    return localStorage.getItem(getUserThemeKey((userData?.email)));
                }
                catch {
                    return null;
                }
            })();
            // Free users must always start in low-power black mode.
            // Do not let old memory/localStorage themes override a Free account.
            if (!isAdmin && !isPro) {
                const defaultTheme = "black";
                setTheme(defaultTheme);
                try {
                    localStorage.setItem(getUserThemeKey((userData?.email)), defaultTheme);
                    localStorage.setItem(getUserThemeKey((userData?.email)) + ":color", defaultTheme);
                    window.dispatchEvent(new CustomEvent("ava-theme-change", { detail: { color: defaultTheme } }));
                }
                catch { }
            }
            else if (saved.theme_color && saved.theme_color !== "black") {
                setTheme(saved.theme_color);
            }
            else if (scopedTheme && scopedTheme !== "black") {
                setTheme(scopedTheme);
            }
            else {
                const defaultTheme = "cyan";
                setTheme(defaultTheme);
                try {
                    localStorage.setItem(getUserThemeKey((userData?.email)), defaultTheme);
                    localStorage.setItem(getUserThemeKey((userData?.email)) + ":color", defaultTheme);
                    window.dispatchEvent(new CustomEvent("ava-theme-change", { detail: { color: defaultTheme } }));
                }
                catch { }
            }
        })
            .catch(() => { });
    }, []);
    // Live bridge: Theme Studio -> Dashboard local theme state.
    useEffect(() => {
        const handler = (event) => {
            const color = event?.detail?.color;
            if (!color || color === "black")
                return;
            setTheme(color);
            try {
                document.body.classList.remove("neon-red", "neon-cyan", "neon-blue", "neon-purple", "neon-green", "neon-orange", "neon-yellow", "neon-pink", "neon-white", "neon-black", "neon-emerald", "neon-amber", "neon-midnight");
                document.body.classList.add(`neon-${color}`);
            }
            catch { }
        };
        window.addEventListener("ava-theme-change", handler);
        window.addEventListener("ava-dashboard-theme-change", handler);
        return () => {
            window.removeEventListener("ava-theme-change", handler);
            window.removeEventListener("ava-dashboard-theme-change", handler);
        };
    }, []);
    const requireProFeature = (featureName) => {
        if (isAdmin || isPro)
            return true;
        alert(interfaceCopy.dashboard.lockedFeature.replace("{feature}", featureName));
        return false;
    };
    const handleUserTyping = () => { };
    const handleGameCommand = (cmd) => {
        if (cmd === "checkers")
            setActiveGame("checkers");
    };
    const handleExit = () => setActiveGame(null);
    async function handleLogout() {
        if (typeof navigator !== "undefined" &&
            navigator.onLine === false) {
            const confirmed = window.confirm("You are currently offline. Logging out will end Local Mode access until you reconnect to the internet. Do you want to continue?");
            if (!confirmed) {
                return;
            }
        }
        try {
            sessionStorage.setItem("ava_logged_out", "1");
        }
        catch { }
        try {
            const keys = [
                "token",
                "access_token",
                "authToken",
                "jwt",
                "user",
                "auth_user",
                "profile",
                "ava_token",
                "ava_user",
                "profileDone"
            ];
            keys.forEach((k) => {
                try {
                    localStorage.removeItem(k);
                }
                catch { }
                try {
                    if (k !== "ava_logged_out")
                        sessionStorage.removeItem(k);
                }
                catch { }
            });
            try {
                sessionStorage.setItem("ava_logged_out", "1");
            }
            catch { }
        }
        catch (e) {
            console.warn("Logout storage clear failed:", e);
        }
        try {
            await fetch("/api/v1/auth/logout", {
                method: "POST",
                credentials: "include",
            });
        }
        catch (e) {
            console.warn("Logout API failed:", e);
        }
        try {
            document.cookie.split(";").forEach((c) => {
                const cookie = c.replace(/^ +/, "");
                const eq = cookie.indexOf("=");
                const name = eq >= 0 ? cookie.slice(0, eq) : cookie;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.avaaio.com`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=sandbox.avaaio.com`;
            });
        }
        catch (e) {
            console.warn("Cookie clear failed:", e);
        }
        window.location.replace(`/login?logged_out=1&t=${Date.now()}`);
    }
    function handleDelete() {
        if (window.confirm("Are you sure? This will permanently erase your account and all stored data.")) {
            fetch("/api/auth/delete-account", {
                method: "DELETE",
                credentials: "include",
            })
                .then(() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login?logged_out=1";
            })
                .catch((err) => {
                console.error("Delete account failed:", err);
            });
        }
    }
    function handleEditProfile() {
        window.location.href = "/setup";
    }
    const [compactViewport, setCompactViewport] = React.useState(false);
    const [showAvaBoot, setShowAvaBoot] = React.useState(true);
    React.useEffect(() => {
        if (!showAvaBoot)
            return;
        const timer = window.setTimeout(() => {
            try {
                sessionStorage.setItem("ava_boot_seen", "1");
            }
            catch { }
            setShowAvaBoot(false);
        }, 2500);
        return () => window.clearTimeout(timer);
    }, [showAvaBoot]);
    // AVA_IPAD_LANDSCAPE_COLD_MOUNT_GATE
    // When an iPad refreshes directly in landscape, Safari may mount
    // WebGL before visualViewport and the dashboard have stable sizes.
    const [avaLandscapeMountReady, setAvaLandscapeMountReady] = React.useState(() => {
        if (typeof window === "undefined" ||
            typeof navigator === "undefined") {
            return true;
        }
        const isiPad = /iPad/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" &&
                navigator.maxTouchPoints > 1);
        return !(isiPad && window.innerWidth > window.innerHeight);
    });
    React.useEffect(() => {
        if (avaLandscapeMountReady)
            return;
        let raf = 0;
        let fallbackTimer = 0;
        let previousSize = "";
        let stableFrames = 0;
        const checkLandscapeViewport = () => {
            const width = Math.round(window.visualViewport?.width || window.innerWidth);
            const height = Math.round(window.visualViewport?.height || window.innerHeight);
            const currentSize = `${width}x${height}`;
            if (width > height &&
                width > 0 &&
                height > 0 &&
                currentSize === previousSize) {
                stableFrames += 1;
            }
            else {
                stableFrames = 0;
                previousSize = currentSize;
            }
            if (stableFrames >= 3) {
                window.dispatchEvent(new Event("resize"));
                setAvaLandscapeMountReady(true);
                return;
            }
            raf = window.requestAnimationFrame(checkLandscapeViewport);
        };
        raf = window.requestAnimationFrame(checkLandscapeViewport);
        fallbackTimer = window.setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
            setAvaLandscapeMountReady(true);
        }, 1800);
        return () => {
            window.cancelAnimationFrame(raf);
            window.clearTimeout(fallbackTimer);
        };
    }, [avaLandscapeMountReady]);
    React.useEffect(() => {
        const syncCompact = () => {
            try {
                const w = window.innerWidth;
                const h = window.innerHeight;
                const vv = window.visualViewport?.height || h;
                const landscape = w > h;
                const compactLandscape = landscape &&
                    (h <= 430 ||
                        (w <= 932 && h <= 480));
                const keyboardOpen = vv < h - (compactLandscape ? 42 : 140);
                setCompactViewport(keyboardOpen);
            }
            catch { }
        };
        syncCompact();
        window.addEventListener("resize", syncCompact);
        window.addEventListener("orientationchange", syncCompact);
        window.visualViewport?.addEventListener("resize", syncCompact);
        return () => {
            window.removeEventListener("resize", syncCompact);
            window.removeEventListener("orientationchange", syncCompact);
            window.visualViewport?.removeEventListener("resize", syncCompact);
        };
    }, []);
    return (_jsxs("div", { className: "dashboard-container min-h-screen flex flex-col bg-black text-white overflow-visible", children: [showAvaBoot && (_jsxs("div", { style: {
                    position: "fixed",
                    inset: 0,
                    zIndex: 99999,
                    background: "radial-gradient(circle at center,#071624 0%,#01060d 55%,#000000 100%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                }, children: [_jsx("style", { children: `
            @keyframes avaPulse {
              0% { transform: scale(1); opacity:.85; }
              50% { transform: scale(1.06); opacity:1; }
              100% { transform: scale(1); opacity:.85; }
            }

            @keyframes avaLoadBar {
              0% { transform: scaleX(0); }
              100% { transform: scaleX(1); }
            }
          ` }), _jsx("div", { style: {
                            fontSize: "clamp(52px,7vw,96px)",
                            fontWeight: 900,
                            letterSpacing: "0.18em",
                            color: "#66eeff",
                            textShadow: "0 0 12px #66eeff,0 0 24px #66eeff,0 0 48px #66eeff",
                            animation: "avaPulse 2s ease-in-out infinite",
                        }, children: "AVAAIO" }), _jsx("div", { style: {
                            marginTop: 14,
                            color: "#d9fcff",
                            fontSize: "14px",
                            letterSpacing: "0.35em",
                        }, children: interfaceCopy.dashboard.loadingSystem }), _jsx("div", { style: {
                            width: 340,
                            maxWidth: "80vw",
                            height: 10,
                            marginTop: 42,
                            borderRadius: 999,
                            background: "rgba(255,255,255,.08)",
                            overflow: "hidden",
                            boxShadow: "0 0 24px rgba(102,238,255,.30)",
                        }, children: _jsx("div", { style: {
                                width: "100%",
                                height: "100%",
                                transformOrigin: "left",
                                animation: "avaLoadBar 2.2s linear forwards",
                                background: "linear-gradient(90deg,#44ccff,#66eeff,#ffffff,#66eeff,#44ccff)",
                            } }) }), _jsx("div", { style: {
                            marginTop: 22,
                            color: "#66eeff",
                            fontWeight: 700,
                            letterSpacing: "0.25em",
                        }, children: interfaceCopy.dashboard.loadingAva })] })), _jsxs("div", { className: `ava-live-stage flex-1 flex flex-col items-center ${compactViewport
                    ? "justify-start pt-[max(calc(env(safe-area-inset-top)+135px),145px)]"
                    : "justify-center"}`, children: [!activeGame && (_jsxs(_Fragment, { children: [_jsx(AvaFace, { speaking: avaSpeaking, mouthLevel: mouthLevel, expression: "NEUTRAL", isPro: !!isPro, isAdmin: !!isAdmin, avaEyeOpen: false, compactViewport: compactViewport &&
                                    window.innerWidth < 700 }, me?.email ?? "guest"), _jsx("div", { className: "ava-response-space" })] })), activeGame === "checkers" && _jsx(Checkers3D, { onExit: () => setActiveGame(null) })] }), _jsx("div", { className: "chatbox-center-wrap chatbox-pos-bottom-center", children: _jsx(ChatBox, {}) }), _jsx(ConnectionModeStatusBadge, {}), _jsx(MobileOrbMenu, {}), false && (_jsx("div", { className: "taskbar desktop-tall", children: _jsxs("div", { className: "taskbar-inner flex justify-between items-center gap-3 px-3 overflow-x-auto", children: [_jsx("div", { className: "left-group flex items-center gap-3", children: _jsx("span", { className: "clock text-lg", children: time }) }), _jsxs("div", { className: "flex flex-nowrap items-center gap-4", children: [_jsxs("div", { className: `group relative ${menuOpen === "games" ? "open" : ""}`, onClick: () => {
                                        if (!requireProFeature("Games"))
                                            return;
                                        setMenuOpen(menuOpen === "games" ? null : "games");
                                    }, children: [_jsx("button", { className: "menu-btn", children: "\uD83C\uDFAE Games" }), _jsxs("div", { className: "menu-dropdown", children: [_jsx("div", { onClick: () => { setActiveGame("checkers"); setMenuOpen(null); }, children: "Checkers" }), _jsx("div", { onClick: () => { alert("Math flash cards coming soon!"); setMenuOpen(null); }, children: "Math Cards" }), _jsx("div", { onClick: () => { alert("More games coming soon!"); setMenuOpen(null); }, children: "More coming soon" })] })] }), _jsxs("div", { className: `group relative ${menuOpen === "media" ? "open" : ""}`, onClick: () => {
                                        if (!requireProFeature("Media"))
                                            return;
                                        setMenuOpen(menuOpen === "media" ? null : "media");
                                    }, children: [_jsx("button", { className: "menu-btn", children: "\uD83C\uDFAC Media & Entertainment" }), _jsx("div", { className: "menu-dropdown", children: _jsx("div", { onClick: () => { alert("Coming soon!"); setMenuOpen(null); }, children: "Coming soon" }) })] }), _jsxs("div", { className: `group relative ${menuOpen === "education" ? "open" : ""}`, onClick: () => {
                                        if (!requireProFeature("Education"))
                                            return;
                                        setMenuOpen(menuOpen === "education" ? null : "education");
                                    }, children: [_jsx("button", { className: "menu-btn", children: "\uD83C\uDF93 Education" }), _jsx("div", { className: "menu-dropdown", children: _jsx("div", { onClick: () => { alert("Education mode coming soon"); setMenuOpen(null); }, children: "Coming soon" }) })] }), _jsxs("div", { className: `group relative ${menuOpen === "theme" ? "open" : ""}`, onClick: () => {
                                        if (!requireProFeature("Theme controls"))
                                            return;
                                        setMenuOpen(menuOpen === "theme" ? null : "theme");
                                    }, children: [_jsx("button", { className: "menu-btn", children: "\uD83C\uDFA8 Theme" }), _jsx("div", { className: "menu-dropdown", children: [
                                                "cyan",
                                                "purple",
                                                "emerald",
                                                "amber",
                                                "lava",
                                                "midnight",
                                                "aurora",
                                            ].map((c) => (_jsx("div", { onClick: () => {
                                                    setTheme(c);
                                                    try {
                                                        localStorage.setItem(getUserThemeKey(me?.email), c);
                                                        localStorage.setItem(getUserThemeKey(me?.email) + ":color", c);
                                                        window.dispatchEvent(new CustomEvent("ava-theme-change", { detail: { color: c } }));
                                                    }
                                                    catch (e) {
                                                        console.warn("[theme event] failed:", e);
                                                    }
                                                    setMenuOpen(null);
                                                }, children: c }, c))) })] }), _jsxs("div", { className: `group relative ${menuOpen === "account" ? "open" : ""}`, onClick: () => setMenuOpen(menuOpen === "account" ? null : "account"), children: [_jsxs("button", { className: "menu-btn flex items-center", children: [_jsx(User, { size: 16, className: "mr-1" }), " Account"] }), _jsxs("div", { className: "menu-dropdown", children: [_jsx("div", { onClick: handleSwitchAccount, children: "Switch Account" }), _jsx("div", { onClick: handleEditProfile, children: "Edit Profile" }), _jsx("div", { className: "text-red-400 hover:text-white", onClick: handleDelete, children: "Delete Account" })] })] })] }), _jsxs("div", { className: "right-group flex items-center gap-3 relative", children: [me && (_jsx("span", { className: "text-cyan-300 text-sm font-semibold mr-2", children: (me?.memory?.display_name || me?.email || 'User') })), _jsx("button", { className: "icon-btn multi", title: "Hide Taskbar", onClick: () => setTaskbarVisible(false), children: _jsx(LayoutGrid, { size: 22 }) }), _jsx("button", { className: "icon-btn multi", title: "Logout", onClick: handleLogout, children: _jsx(Power, { size: 22 }) })] })] }) })), false && (_jsx("button", { className: "fixed bottom-4 right-4 px-4 py-2 bg-[var(--color)] text-black font-bold rounded hover:opacity-90", onClick: () => setTaskbarVisible(true), children: "Show Taskbar" }))] }));
}
